import { useMemo } from "react";

import type { CollectionHeadingSync } from "../components/CollectionHeading";
import { isSyncBusy, useInvalidateOnSync, useSyncStatus } from "./useSyncState";

type UseSyncHeaderInput = {
  datasourceId: string;
  /** Status routes exist for Google Sheets datasources only (ARD 9.1, 9.6). */
  enabled: boolean;
  /** A list answer arrived with `stale: true`; it reads from the read cache. */
  stale?: boolean;
  staleFetchedAt?: string | null;
};

/**
 * The status poll and the derived header props. The desktop Manager and the
 * phone heading both call it, but only one of them mounts, so one poll runs.
 */
export const useSyncHeader = ({
  datasourceId,
  enabled,
  stale,
  staleFetchedAt,
}: UseSyncHeaderInput): {
  sync?: CollectionHeadingSync;
  busy: boolean;
  lastSyncedAt: string | null | undefined;
} => {
  const status = useSyncStatus(datasourceId, enabled);

  useInvalidateOnSync(datasourceId, status.data?.lastSyncedAt);

  const sync = useMemo<CollectionHeadingSync | undefined>(() => {
    const data = status.data;
    if (!data && !stale) return undefined;
    return {
      lastSyncedAt: data?.lastSyncedAt ?? null,
      pending: data?.pending ?? 0,
      failed: data?.failed ?? 0,
      pausedUntil: data?.pausedUntil ?? null,
      stale: !!stale,
      staleFetchedAt: staleFetchedAt ?? null,
    };
  }, [status.data, stale, staleFetchedAt]);

  return {
    sync,
    busy: isSyncBusy(status.data),
    lastSyncedAt: status.data?.lastSyncedAt,
  };
};
